import deserializeServiceGroup from "~/service/helpers/deserializeServiceGroup";
import paginationDbParams from "~/kernel/pagination/paginationDbParams";
import type { Image } from "@prisma/client";
import type { MetaFunction } from "@remix-run/node";
import { redirect, useLoaderData as useLoaderDataBase } from "@remix-run/react";
import type { LoaderFunction } from "@remix-run/server-runtime";
import Heading from "~/ui/Heading";
import prisma from "~/kernel/db/prisma.server";
import directionsUrl from "~/company/helpers/directionsUrl";
import Link from "~/ui/Link";
import Icon from "~/ui/Icon";
import type { IconName } from "~/ui/Icon";
import fullAddress from "~/kernel/adress/fullAddress";
import OpeningHours from "~/routes/companies.$slug._index/OpeningHours";
import type OpeningHoursModel from "~/models/company/TOpeningHours";
import { days } from "~/kernel/dateTime/Day";
import Card from "~/ui/Card";
import imageUrl from "~/employee/helpers/imageUrl";
import ROUTES from "~/kernel/routing/ROUTES";
import ReadMore from "~/ui/ReadMore";
import { bannerUrl } from "~/company/helpers/bannerUrl";
import title from "~/kernel/meta/title";
import BANNER_DIMENSIONS from "~/constants/company/BANNER_DIMENSIONS";
import ReviewsSection, {
  scrollToReviews,
} from "~/routes/companies.$slug._index/ReviewsSection";
import Score from "~/routes/companies.$slug._index/Score";
import Services from "~/routes/companies.$slug._index/Services";
import Employees from "~/routes/companies.$slug._index/Employees";
import deserializeCompany from "~/company/helpers/deserializeCompany";
import { deserializeService } from "~/service/shared/index";
import type { PaginatedResponse } from "~/kernel/pagination/paginatedResponse";
import paginatedResponse from "~/kernel/pagination/paginatedResponse";
import getSearchParams from "~/kernel/searchParams/getSearchParams";
import getPaginationSearchParams from "~/kernel/pagination/getPaginationSearchParams";
import pagination from "~/kernel/pagination/pagination";
import json from "~/kernel/server/json";
import FlashMessage from "~/ui/FlashMessage";
import imageable from "~/kernel/image/imageable";
import companyRepository from "~/repositories/companyRepository";
import requireResource from "~/kernel/server/requireResource";
import hasBookingTerms from "~/company/helpers/hasBookingTerms";
import employeeRepository from "~/repositories/employeeRepository";
import BookButton from "./BookButton";
import { Phone } from "~/kernel/utilities/index";
import useTranslation from "~/kernel/internationalization/useTranslation";
import isPresent from "~/kernel/validation/isPresent";
import isNil from "~/kernel/validation/isNil";
import { translationHandle } from "~/kernel/internationalization";
import type { CompanyPageService } from "./findCompanyPageServices";
import { findCompanyPageServices } from "./findCompanyPageServices";
import type { GetCompany } from "./GetCompany";
import { buildSearchEngineMetadata } from "./buildSearchEngineMetadata";
import serviceGroupRepository from "~/repositories/serviceGroupRepository";
import type { CompanyPageServiceGroup } from "./CompanyPageServiceGroup";
import type { CompanyPageReview } from "./CompanyPageReview";
import { buildCompanyPageReview } from "./buildCompanyPageReview";
import getUser from "~/kernel/auth/getUser.server";

export const handle = translationHandle({
  ns: {
    companies: true,
    services: true,
  },
});

export type LoaderData = {
  company: GetCompany;
  services: CompanyPageService[];
  serviceGroups: CompanyPageServiceGroup[];
  reviews: PaginatedResponse<CompanyPageReview>;
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const company: GetCompany | undefined = data?.company;

  if (isNil(company)) {
    return [];
  }

  return [
    {
      title: title(company.name),
    },
    ...buildSearchEngineMetadata(company, data?.reviews?.data ?? []),
  ];
};

const REVIEWS_PAGE_SIZE = 5;

export const loader: LoaderFunction = async ({ request, params }) => {
  const searchParams = getSearchParams(request);
  const { page } = getPaginationSearchParams(searchParams);
  const { slug } = params;
  requireResource(slug);
  const [_company, serviceGroups, employees, services, reviews] =
    await Promise.all([
      companyRepository.findBySlug(slug),
      serviceGroupRepository.findManyByCompanySlug(slug),
      employeeRepository.findManyByCompanySlug(slug),
      findCompanyPageServices(slug),
      prisma.review.findMany({
        where: { company: { slug: params.slug } },
        orderBy: { createdAt: "desc" },
        select: {
          id: true,
          companyId: true,
          bookingId: true,
          reviewerName: true,
          reviewText: true,
          score: true,
          userId: true,
          createdAt: true,
          comments: {
            orderBy: { createdAt: "asc" },
            select: {
              id: true,
              content: true,
              companyId: true,
              createdAt: true,
              user: { select: { name: true } },
              company: { select: { name: true } },
            },
          },
        },
        ...paginationDbParams({
          page,
          pageSize: REVIEWS_PAGE_SIZE,
        }),
      }),
    ]);

  if (!_company) {
    return redirect(ROUTES.companyNotFound.path({ slug }));
  }

  const company = await imageable.company.banner.attach(_company);
  const user = await getUser(request);

  const scoreResult = await prisma.review.aggregate({
    where: { companyId: company?.id },
    _avg: { score: true },
    _count: { score: true },
  });

  return json<LoaderData>(request, {
    reviews: paginatedResponse({
      ...pagination({
        totalCount: scoreResult._count.score,
        pageSize: REVIEWS_PAGE_SIZE,
        page,
      }),
      getPagePath: (opts) =>
        ROUTES.company.path({
          params: { slug: company.slug },
          query: { page: opts.page },
        }),
      data: reviews.map((review) => buildCompanyPageReview(review, user)),
    }),
    services,
    serviceGroups,
    company: {
      ...company,
      score: scoreResult._avg.score,
      reviewCount: scoreResult._count.score,
      bannerUrl: bannerUrl(company),
      employees: await imageable.employee.image
        .attachToMany(employees)
        .then((employees) =>
          employees.map((employee) => ({
            ...employee,
            imageUrl: employee.image
              ? imageUrl(employee)
              : "/fallback-employee-image.svg",
          })),
        ),
    },
  });
};

function useLoaderData() {
  const {
    company: loaderCompany,
    services: loaderServices,
    serviceGroups: loaderServiceGroups,
  } = useLoaderDataBase<LoaderData>();

  const company = {
    ...loaderCompany,
    ...deserializeCompany(loaderCompany),
  };
  const services = loaderServices.map((service) => ({
    ...deserializeService(service),
    addOnAssignments: service.addOnAssignments.map((ass) => ({
      ...ass,
      addOnService: deserializeService(ass.addOnService),
    })),
  }));
  const serviceGroups = loaderServiceGroups.map((group) =>
    deserializeServiceGroup(group),
  );
  return { company, services, serviceGroups };
}

export default function CompanyPage() {
  const { company, services, serviceGroups } = useLoaderData();

  const { locale, tt } = useTranslation("companyPage");
  return (
    <div className="space-y-2">
      <FlashMessage />
      <div className="mx-auto max-w-5xl">
        <Card>
          <div className="flex flex-col gap-8 md:flex-row md:gap-0">
            <div className="border-r-200 space-y-5 md:w-1/2 md:border-r md:pr-10">
              <Banner company={company} />
              <div className="space-y-2">
                <Heading level={2}>{company.name}</Heading>
                <Score
                  onClick={() => scrollToReviews()}
                  value={company.score}
                  reviewCount={company.reviewCount}
                />
              </div>

              <BookButton company={company} />
              <div className="grid grid-cols-[min-content,1fr]">
                <InfoField icon="circle-info" show={hasBookingTerms(company)}>
                  <Link to={ROUTES.bookingTerms.path(company)}>
                    {tt("bookingTerms")}
                  </Link>
                </InfoField>
                <InfoField icon="location-dot" show={!!fullAddress(company)}>
                  {fullAddress(company)}
                  <br />
                  <Link target="_blank" to={directionsUrl(company)} external rel="noreferrer">
                    {tt("showMap")}
                  </Link>
                </InfoField>
                <InfoField icon="envelope" show={!!company.contactEmail}>
                  <Link
                    target="_blank"
                    to={`mailto:${company.contactEmail}`}
                    external rel="noreferrer"
                  >
                    {company.contactEmail}
                  </Link>
                </InfoField>
                <InfoField icon="phone" show={!!company.phone}>
                  {isPresent(company.phone) ? (
                    <Link target="_blank" to={`tel:${company.phone}`} external rel="noreferrer">
                      {Phone.present(company.phone, { locale })}
                    </Link>
                  ) : null}
                </InfoField>
                <InfoField icon="clock" show={hasOpeningHours(company)}>
                  <OpeningHours openingHours={company} />
                </InfoField>
                <InfoField icon="circle-info" show={!!company.description}>
                  <ReadMore text={company.description} />
                </InfoField>
              </div>
            </div>

            <div className="space-y-12 md:w-1/2 md:pl-10">
              <Services services={services} groups={serviceGroups} />
              <Employees employees={company.employees} />
              <ReviewsSection />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function InfoField({
  icon,
  children,
  show = true,
}: {
  icon: IconName;
  children: React.ReactNode;
  show?: boolean;
}) {
  if (show) {
    return (
      <>
        <div className="px-2 py-2 align-top">
          <Icon className="text-slate-600" name={icon} />
        </div>
        <div className="px-2 py-2 break-words min-w-[0]">{children}</div>
      </>
    );
  }

  return null;
}

function Banner({
  company,
}: {
  company: Pick<GetCompany, "bannerUrl" | "name"> & {
    banner: Pick<Image, "width" | "height"> | null;
  };
}) {
  const { tt } = useTranslation("companyPage");
  return (
    <div className="relative max-h-96 w-full overflow-hidden rounded-md bg-slate-100">
      <img
        className="relative h-full w-full bg-slate-200"
        src={company.bannerUrl}
        alt={tt("bannerAlt", { companyName: company.name })}
        width={company.banner?.width ?? BANNER_DIMENSIONS.width}
        height={company.banner?.height ?? BANNER_DIMENSIONS.height}
      />
    </div>
  );
}

function hasOpeningHours(openingHours: OpeningHoursModel) {
  return days.some(
    (day) =>
      typeof openingHours[`${day}OpensMinute`] === "number" &&
      typeof openingHours[`${day}ClosesMinute`] === "number",
  );
}
